/* eslint-disable no-console */

const CACHE_PREFIX = 'sf24:cache:';

export const DEFAULT_TTL_MS = 30_000;
export const DEFAULT_TIMEOUT_MS = 10_000;
export const DEFAULT_MAX_RETRIES = 3;
/** Spread simultaneous refreshes so the API is not hit in the same second. */
/** Light jitter on first network attempt — enough to spread herds, low enough for LCP. */
export const DEFAULT_STAGGER_MS = 800;

export interface CacheEntry<T> {
  data: T;
  cachedAt: number;
}

export interface ResilientFetchResult<T> {
  data: T | null;
  fromCache: boolean;
  stale: boolean;
  error: boolean;
}

export interface ResilientFetchOptions<T> {
  key: string;
  fetcher: () => Promise<T>;
  isSuccess: (value: T) => boolean;
  ttlMs?: number;
  timeoutMs?: number;
  maxRetries?: number;
  /** Max random delay before the first network attempt when cache is missing/stale. */
  staggerMs?: number;
  /** Skip network when a fresh cache entry exists. Default true. */
  skipIfFresh?: boolean;
}

const inflight = new Map<string, Promise<ResilientFetchResult<unknown>>>();

function storageKey(key: string): string {
  return `${CACHE_PREFIX}${key}`;
}

export function readSessionCache<T>(key: string): CacheEntry<T> | null {
  try {
    const raw = sessionStorage.getItem(storageKey(key));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CacheEntry<T>;
    if (!parsed || typeof parsed.cachedAt !== 'number') return null;
    return parsed;
  } catch {
    return null;
  }
}

export function writeSessionCache<T>(key: string, data: T): void {
  try {
    const entry: CacheEntry<T> = { data, cachedAt: Date.now() };
    sessionStorage.setItem(storageKey(key), JSON.stringify(entry));
  } catch (err) {
    // Quota / private mode — ignore; network path still works
    console.warn('sessionStorage write failed', err);
  }
}

export function isCacheFresh(entry: CacheEntry<unknown>, ttlMs = DEFAULT_TTL_MS): boolean {
  return Date.now() - entry.cachedAt < ttlMs;
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function randomJitter(maxMs: number): number {
  if (maxMs <= 0) return 0;
  return Math.floor(Math.random() * maxMs);
}

function withTimeout<T>(promise: Promise<T>, timeoutMs: number): Promise<T> {
  if (timeoutMs <= 0) return promise;

  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error('Request timed out')), timeoutMs);
    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (err) => {
        clearTimeout(timer);
        reject(err);
      },
    );
  });
}

function backoffDelay(attempt: number): number {
  // 1s, 2s, 4s … with ±30% jitter
  const base = Math.min(1000 * 2 ** attempt, 8000);
  const jitter = base * 0.3;
  return Math.floor(base - jitter + Math.random() * jitter * 2);
}

/**
 * Soft-error shape returned by doGet/doPost catch blocks (no real payload).
 */
export function isRequestErrorPayload(response: unknown): boolean {
  if (!response || typeof response !== 'object') return true;
  const data = (response as { data?: unknown }).data;
  if (data == null || typeof data !== 'object') return true;

  const record = data as Record<string, unknown>;
  const hasStatusCode = typeof record.statusCode === 'number';
  const hasDomainFields =
    'subCategories' in record ||
    'game' in record ||
    'subCategoryName' in record;

  if (hasStatusCode && !hasDomainFields) return true;
  return false;
}

export function isFixtureListResponse(response: unknown): boolean {
  if (isRequestErrorPayload(response)) return false;
  const data = (response as { data?: { subCategories?: unknown } }).data;
  return Array.isArray(data?.subCategories);
}

export function isFixtureDetailResponse(response: unknown): boolean {
  if (isRequestErrorPayload(response)) return false;
  const data = (response as { data?: unknown }).data;
  return data != null && typeof data === 'object';
}

async function runFetch<T>(
  options: ResilientFetchOptions<T>,
): Promise<ResilientFetchResult<T>> {
  const {
    key,
    fetcher,
    isSuccess,
    ttlMs = DEFAULT_TTL_MS,
    timeoutMs = DEFAULT_TIMEOUT_MS,
    maxRetries = DEFAULT_MAX_RETRIES,
    staggerMs = DEFAULT_STAGGER_MS,
    skipIfFresh = true,
  } = options;

  const cached = readSessionCache<T>(key);

  if (cached && skipIfFresh && isCacheFresh(cached, ttlMs)) {
    return {
      data: cached.data,
      fromCache: true,
      stale: false,
      error: false,
    };
  }

  // Spread thundering herd when many tabs refresh at once
  if (staggerMs > 0) {
    await sleep(randomJitter(staggerMs));
  }

  let lastError = false;

  for (let attempt = 0; attempt <= maxRetries; attempt += 1) {
    if (attempt > 0) {
      await sleep(backoffDelay(attempt - 1));
    }

    try {
      const value = await withTimeout(fetcher(), timeoutMs);
      if (isSuccess(value)) {
        writeSessionCache(key, value);
        return {
          data: value,
          fromCache: false,
          stale: false,
          error: false,
        };
      }
      lastError = true;
    } catch {
      lastError = true;
    }
  }

  if (cached) {
    return {
      data: cached.data,
      fromCache: true,
      stale: true,
      error: true,
    };
  }

  return {
    data: null,
    fromCache: false,
    stale: false,
    error: lastError,
  };
}

/**
 * Resilient fetch with sessionStorage cache, timeout, retry+jitter, and in-flight dedupe.
 */
export function resilientFetch<T>(
  options: ResilientFetchOptions<T>,
): Promise<ResilientFetchResult<T>> {
  const existing = inflight.get(options.key);
  if (existing) {
    return existing as Promise<ResilientFetchResult<T>>;
  }

  const promise = runFetch(options).finally(() => {
    inflight.delete(options.key);
  });

  inflight.set(options.key, promise as Promise<ResilientFetchResult<unknown>>);
  return promise;
}

export function fixtureListCacheKey(categoryName?: string): string {
  return `fixtures:${(categoryName || 'Streams Today').toLowerCase()}`;
}

export function fixtureDetailCacheKey(teams: string): string {
  return `fixture:${teams.toLowerCase()}`;
}
